import axiosInstance from "./api";
import { toast } from "react-toastify";
import { getAgentId } from "../helpers";

function humanizeText(str) {
  // Add a space before each capital letter (except the first)
  return str.replace(/([a-z])([A-Z])/g, "$1 $2");
}

export const regenerateKeywordCluster = async ({
  agentId,
  sessionId,
  uid,
  blogType,
  seedKeywords,
  furtherDetails,
  sectionName,
}) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");

  // Get the KeywordClusterSmith agent ID from localStorage
  const keywordClusterAgentId = getAgentId("KeywordClusterSmith");
  if (!keywordClusterAgentId) {
    throw new Error("KeywordClusterSmith agent ID not found. Please log in again.");
  }

  // Build variables object for the new API format
  const variables = {
    uid: uid || "",
    step: 1,
    blog_type: blogType || "",
    seed_keywords: seedKeywords?.join(",") || "",
    further_details: furtherDetails || "",
    section_name: sectionName || "",
  };

  // Build query string with keywords
  const query = seedKeywords?.join(", ") || "";

  // Build FormData for the new API endpoint
  const formData = new FormData();
  formData.append('agent_id', keywordClusterAgentId.toString());
  formData.append('session_id', sessionId);
  formData.append('streaming', 'true');
  
  // Add query if keywords are provided
  if (query) {
    formData.append('query', query);
  }
  
  // Add variables as JSON string
  const variablesString = JSON.stringify(variables);
  formData.append('variables', variablesString);

  const response = await fetch(
    `${axiosInstance.defaults.baseURL}/api/v1/fouray/execute`,
    {
      method: "POST",
      headers: {
        Authorization: token,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`HTTP Error ${response.status}:`, errorText);
    throw new Error(
      `HTTP ${response.status}: ${errorText || response.statusText}`
    );
  }

  if (!response.body) {
    console.error("No response body received");
    throw new Error("No response body received");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let jsonString = "";
  let done = false;

  while (!done) {
    const { value, done: doneReading } = await reader.read();
    done = doneReading;

    if (value) {
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      
      for (const line of lines) {
        if (line.startsWith("data: ")) {
          // Remove "data: " prefix but preserve all spaces in the content
          const cleaned = line.replace(/^data:\s?/, "");
          
          // Skip empty lines (after removing prefix) and [DONE] marker
          const trimmed = cleaned.trim();
          if (trimmed === "" || trimmed === "[DONE]") {
            continue;
          }
          
          // Skip only the complete run_meta JSON object (it comes as a single complete JSON on one line)
          if (trimmed.startsWith('{') && trimmed.includes('"type"') && trimmed.includes('"run_meta"')) {
            continue;
          }
          
          // Accumulate all other JSON data chunks (preserving spaces)
          jsonString += cleaned;
        }
      }
    }
  }

  // Handle any remaining buffer
  if (buffer.trim().startsWith("data: ")) {
    const cleanedData = buffer.replace(/^data:\s*/, "");
    const trimmed = cleanedData.trim();
    if (trimmed && trimmed !== "[DONE]") {
      // Skip if it's the run_meta object
      if (!(trimmed.startsWith('{') && trimmed.includes('"type"') && trimmed.includes('"run_meta"'))) {
        jsonString += cleanedData;
      }
    }
  }

  try {
    // Try to parse the accumulated JSON
    const parsed = JSON.parse(jsonString);
    
    // Transform the response format to match expected structure
    // The API returns { metadata: {...}, clusters: [...], ... }
    // We need to transform it to { semantic_clusters: [...] }
    if (parsed.clusters && Array.isArray(parsed.clusters)) {
      // Transform clusters to semantic_clusters format
      const semanticClusters = parsed.clusters.map((cluster, index) => ({
        id: cluster.cluster_id || `C${index + 1}`,
        cluster_name: cluster.label || cluster.target_h2 || "",
        keywords: cluster.keywords || [],
        intent: cluster.intent || "",
        priority: cluster.priority || "",
        notes: cluster.notes || "",
      }));
      
      return {
        semantic_clusters: semanticClusters,
        blog_type: parsed.metadata?.blog_type || "",
        metadata: parsed.metadata || {},
      };
    }
    
    return parsed;
  } catch (e) {
    console.error("Failed to parse streamed JSON:", e);
    console.error("JSON string length:", jsonString.length);
    console.error("JSON string preview:", jsonString.substring(0, 500));
    
    // If parsing fails, return the raw string content if it's not empty
    if (jsonString.trim() !== "") {
      return { new_content: jsonString.trim() };
    } else {
      throw new Error(
        "Failed to parse response data and no raw content available."
      );
    }
  }
};

export const generateBlogOutline = async (
  topic,
  tone,
  wordCountTarget,
  location,
  language,
  semanticClusters = [],
  brandContext,
  excludeTopics,
  ctaMode,
  ctaProductName,
  ctaUrl,
  faqCount,
  sessionId,
  agentId,
  uid,
  query, // Query parameter - will use topic value
  onChunk = (chunk) => {} // Callback for streaming updates
) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");

  // Get the OutlineArchitect agent ID from localStorage
  const outlineArchitectAgentId = getAgentId("OutlineArchitect");
  if (!outlineArchitectAgentId) {
    throw new Error("OutlineArchitect agent ID not found. Please log in again.");
  }

  // Build variables object for the new API format
  const variables = {
    uid: uid || "",
    step: 2,
    topic: topic || "",
    tone: tone || "",
    word_count_target: wordCountTarget || "",
    location_focus: location || "",
    language: language || "",
    keyword_clusters_json: JSON.stringify(semanticClusters || []),
    brand_context: brandContext || "",
    exclude_topics: excludeTopics || "",
    cta_mode: ctaMode || "",
    cta_product_name: ctaProductName || "",
    cta_url: ctaUrl || "",
    faq_count: faqCount || "",
  };

  // Use topic value as query
  const queryString = query || topic || "";

  // Build FormData for the new API endpoint
  const formData = new FormData();
  formData.append('agent_id', outlineArchitectAgentId.toString());
  formData.append('session_id', sessionId);
  formData.append('streaming', 'true');
  formData.append('query', queryString);
  
  // Add variables as JSON string
  const variablesString = JSON.stringify(variables);
  formData.append('variables', variablesString);

  try {
    const response = await fetch(
      `${axiosInstance.defaults.baseURL}/api/v1/fouray/execute`,
      {
        method: "POST",
        headers: {
          Authorization: token,
        },
        body: formData,
      }
    );

    if (!response.ok || !response.body) {
      throw new Error("Network response error or no body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let jsonString = "";
    let done = false;

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;

      if (value) {
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            // Remove "data: " prefix but preserve all spaces in the content
            const cleaned = line.replace(/^data:\s?/, "");
            
            // Skip empty lines (after removing prefix) and [DONE] marker
            const trimmed = cleaned.trim();
            if (trimmed === "" || trimmed === "[DONE]") {
              continue;
            }
            
            // Skip only the complete run_meta JSON object (it comes as a single complete JSON on one line)
            if (trimmed.startsWith('{') && trimmed.includes('"type"') && trimmed.includes('"run_meta"')) {
              continue;
            }
            
            // Accumulate all other JSON data chunks (preserving spaces)
            jsonString += cleaned;
            if (onChunk) onChunk(cleaned);
          }
        }
      }
    }

    // Handle any remaining buffer
    if (buffer.trim().startsWith("data: ")) {
      const cleanedData = buffer.replace(/^data:\s*/, "");
      const trimmed = cleanedData.trim();
      if (trimmed && trimmed !== "[DONE]") {
        // Skip if it's the run_meta object
        if (!(trimmed.startsWith('{') && trimmed.includes('"type"') && trimmed.includes('"run_meta"'))) {
          jsonString += cleanedData;
          if (onChunk) onChunk(cleanedData);
        }
      }
    }

    try {
      const parsedData = JSON.parse(jsonString);
      return parsedData;
    } catch (e) {
      console.error("Failed to parse streamed JSON:", e);
      console.error("JSON string length:", jsonString.length);
      console.error("JSON string preview:", jsonString.substring(0, 500));
      // If parsing fails, return null or throw an error
      return null;
    }
  } catch (error) {
    console.error("Error generating blog:", error.response || error.message);
    throw error;
  }
};

export const regenerateBlogSection = async (
  {
    agentId,
    sessionId,
    uid,
    blogType,
    sectionName,
    customPrompt,
    sectionContent,
  },
  onStreamChunk
) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");

  const body = {
    request_type: "blog",
    additional_data: {
      agent_id: agentId,
      session_id: sessionId,
      uid: uid,
      step: 3,
      blog_type: blogType,
      previous_response: sectionContent,
      further_details: customPrompt,
      section_name: sectionName,
    },
  };

  const response = await fetch(
    `${axiosInstance.defaults.baseURL}/generates/genrate_blogs`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`generateFullBlog HTTP Error ${response.status}:`, errorText);
    throw new Error(
      `HTTP ${response.status}: ${errorText || response.statusText}`
    );
  }

  if (!response.body) {
    console.error("generateFullBlog: No response body received");
    throw new Error("No response body received");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let jsonString = "";
  let done = false;

  while (!done) {
    const { value, done: doneReading } = await reader.read();
    done = doneReading;

    if (value) {
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (line.startsWith("data:")) {
          const cleaned = line.replace(/^data:\s?/, "");
          if (cleaned !== "") {
            if (onStreamChunk) {
              onStreamChunk(cleaned);
            }
            jsonString += cleaned;
          }
        }
      }
    }
  }

  if (buffer.trim().startsWith("data:")) {
    const cleanedData = buffer.replace(/^data:\s*/, " ").trim();
    if (cleanedData) {
      if (onStreamChunk) {
        onStreamChunk(cleanedData);
      }
      jsonString += cleanedData;
    }
  }

  try {
    return JSON.parse(jsonString);
  } catch (e) {
    console.error("Failed to parse streamed JSON:", e);
    // If parsing fails, return the raw string content if it's not empty
    if (jsonString.trim() !== "") {
      return { new_content: jsonString.trim() };
    } else {
      throw new Error(
        "Failed to parse response data and no raw content available."
      );
    }
  }
};

export const regenerateBlogOutline = async (
  blogAbout,
  tone,
  wordLimit,
  location,
  language,
  furtherDetails,
  semanticClusters = [],
  sessionId,
  agentId,
  uid,
  previousOutline,
  onChunk = (chunk) => {},
  sectionParams = {}
) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");

  const payload = {
    request_type: "blog",
    additional_data: {
      agent_id: agentId,
      session_id: sessionId,
      step: 2,
      blog_about: blogAbout,
      tone_of_voice: tone,
      blog_length: wordLimit,
      location_focus: location,
      language: language,
      further_details: furtherDetails,
      semantic_clusters: semanticClusters,
      uid: uid,
      previous_response: previousOutline,
      streaming: true,
      ...sectionParams,
    },
  };

  try {
    const response = await fetch(
      `${axiosInstance.defaults.baseURL}/generates/genrate_blogs`,
      {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok || !response.body) {
      throw new Error("Network response error or no body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let jsonString = "";
    let done = false;

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;

      if (value) {
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data:")) {
            const cleaned = line.replace(/^data:\s?/, "");
            if (cleaned !== "") {
              jsonString += cleaned;
              if (onChunk) onChunk(cleaned);
            }
          }
        }
      }
    }

    if (buffer.trim().startsWith("data:")) {
      const chunk = buffer.replace(/^data:\s*/, " ");
      jsonString += chunk;
      onChunk(chunk);
    }

    try {
      console.log("Attempting to parse JSON string:", jsonString);
      const parsedData = JSON.parse(jsonString);
      console.log("Parsed data:", parsedData);
      return parsedData;
    } catch (e) {
      console.error("Failed to parse streamed JSON:", e);
      console.error("Malformed JSON string:", jsonString);
      return null;
    }
  } catch (error) {
    console.error(
      "Error regenerating blog outline:",
      error.response || error.message
    );
    throw error;
  }
};

export const getBlogById = async (blogId) => {
  // const token = localStorage.getItem("token");
  // if (!token) throw new Error("Unauthorized: No token found");

  try {
    const response = await axiosInstance.get(`/api/v1/blogs/${blogId}`, {
      // headers: {
      //   // Authorization: token,
      // },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching blog:", error.response || error.message);
    throw error;
  }
};

export const updateBlog = async (blogId, blogData) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");

  try {
    const response = await axiosInstance.put(`/blog/${blogId}`, blogData, {
      headers: {
        Authorization: token,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error updating blog:", error.response || error.message);
    throw error;
  }
};

export const deleteBlog = async (blogId) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");
  try {
    const response = await axiosInstance.delete(`/api/v1/blogs/${blogId}`, {
      headers: {
        Authorization: token,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error deleting blog:", error.response || error.message);
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const publishBlog = async (blogId) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");

  try {
    const response = await axiosInstance.post(
      `/blog/${blogId}/publish`,
      {},
      {
        headers: {
          Authorization: token,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error publishing blog:", error.response || error.message);
    throw error;
  }
};
export async function generateFullBlog(
  topic,
  tone,
  wordCountTarget,
  location,
  language,
  semanticClusters = [],
  brandContext,
  excludeTopics,
  ctaMode,
  ctaProductName,
  ctaUrl,
  faqCount,
  topicMapJson,
  blogType,
  sessionId,
  agentId,
  uid,
  query,
  format = "html",
  onChunk = (chunk) => {}
) {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");

  // Get the BlogForgeFinal agent ID from localStorage
  const blogForgeFinalAgentId = getAgentId("BlogForgeFinal");
  if (!blogForgeFinalAgentId) {
    throw new Error("BlogForgeFinal agent ID not found. Please log in again.");
  }

  // Build variables object for the new API format
  const variables = {
    uid: uid || "",
    step: 3,
    topic: topic || "",
    tone: tone || "",
    word_count_target: wordCountTarget || "",
    location_focus: location || "",
    language: language || "",
    keyword_clusters_json: JSON.stringify(semanticClusters || []),
    brand_context: brandContext || "",
    exclude_topics: excludeTopics || "",
    cta_mode: ctaMode || "",
    cta_product_name: ctaProductName || "",
    cta_url: ctaUrl || "",
    faq_count: faqCount || "",
    topic_map_json: typeof topicMapJson === 'string' ? topicMapJson : JSON.stringify(topicMapJson || {}),
    blog_type: blogType || "",
    format: format || "html",
  };

  // Use topic value as query
  const queryString = query || topic || "";

  // Build FormData for the new API endpoint
  const formData = new FormData();
  formData.append('agent_id', blogForgeFinalAgentId.toString());
  formData.append('session_id', sessionId);
  formData.append('streaming', 'true');
  formData.append('query', queryString);
  
  // Add variables as JSON string
  const variablesString = JSON.stringify(variables);
  formData.append('variables', variablesString);

  console.log("generateFullBlog variables:", variables);
  console.log("generateFullBlog format parameter received:", format);
  console.log("generateFullBlog format in variables:", variables.format);
  console.log("API URL:", `${axiosInstance.defaults.baseURL}/api/v1/fouray/execute`);

  try {
    const response = await fetch(
      `${axiosInstance.defaults.baseURL}/api/v1/fouray/execute`,
      {
        method: "POST",
        headers: {
          Authorization: token,
        },
        body: formData,
      }
    );

    if (!response.ok || !response.body) {
      throw new Error("Network response error or no body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let jsonString = "";
    let done = false;

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;

      if (value) {
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            // Remove "data: " prefix but preserve all spaces in the content
            const cleaned = line.replace(/^data:\s?/, "");
            
            // Skip empty lines (after removing prefix) and [DONE] marker
            const trimmed = cleaned.trim();
            if (trimmed === "" || trimmed === "[DONE]") {
              continue;
            }
            
            // Skip only the complete run_meta JSON object (it comes as a single complete JSON on one line)
            if (trimmed.startsWith('{') && trimmed.includes('"type"') && trimmed.includes('"run_meta"')) {
              continue;
            }
            
            // Accumulate all other JSON data chunks (preserving spaces)
            jsonString += cleaned;
            if (onChunk) onChunk(cleaned);
          }
        }
      }
    }

    // Handle any remaining buffer
    if (buffer.trim().startsWith("data: ")) {
      const cleanedData = buffer.replace(/^data:\s*/, "");
      const trimmed = cleanedData.trim();
      if (trimmed && trimmed !== "[DONE]") {
        // Skip if it's the run_meta object
        if (!(trimmed.startsWith('{') && trimmed.includes('"type"') && trimmed.includes('"run_meta"'))) {
          jsonString += cleanedData;
          if (onChunk) onChunk(cleanedData);
        }
      }
    }

    try {
      const parsedData = JSON.parse(jsonString);
      return parsedData;
    } catch (e) {
      console.error("Failed to parse streamed JSON:", e);
      console.error("JSON string length:", jsonString.length);
      console.error("JSON string preview:", jsonString.substring(0, 500));
      // If parsing fails, return null or throw an error
      return null;
    }
  } catch (networkError) {
    console.error("Network error in generateFullBlog:", networkError);
    throw new Error(`Network error: ${networkError.message}`);
  }
}

function tryParseSSEStyleBlogJson(data) {
  const lines = data.split("\n");
  let jsonChunks = [];

  for (const line of lines) {
    if (line.trim().startsWith("data:")) {
      const content = line.replace(/^data:\s*/, "");
      if (content !== "[DONE]" && content !== "") {
        jsonChunks.push(content);
      }
    }
  }

  const combined = jsonChunks.join("");

  // This assumes the full blog JSON is sent as stringified JSON in the stream
  const firstBrace = combined.indexOf("{");
  const lastBrace = combined.lastIndexOf("}");
  const jsonStr = combined.slice(firstBrace, lastBrace + 1);

  const parsed = JSON.parse(jsonStr);

  if (!parsed.sections || !Array.isArray(parsed.sections)) {
    throw new Error("Invalid blog structure");
  }

  return parsed;
}

export const saveBlogDraftV2 = async (draftData) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");

  const formData = new FormData();

  // Only append if value is present
  if (draftData.name) formData.append("blog[name]", draftData.name);
  if (draftData.uid) formData.append("blog[uid]", draftData.uid);
  if (draftData.tone) formData.append("blog[tone]", draftData.tone);
  if (draftData.about) formData.append("blog[about]", draftData.about);
  if (draftData.word_limit)
    formData.append("blog[word_limit]", draftData.word_limit);
  if (draftData.location) formData.append("blog[location]", draftData.location);
  if (draftData.language) formData.append("blog[language]", draftData.language);
  if (draftData.session_id)
    formData.append("blog[session_id]", draftData.session_id);
  if (draftData.agent_id) formData.append("blog[agent_id]", draftData.agent_id);
  if (draftData.further_details)
    formData.append("blog[further_details]", draftData.further_details);
  formData.append("blog[status]", draftData.status || "draft");

  // Only append images if present and non-empty
  // if (Array.isArray(draftData.images) && draftData.images.length > 0) {
  //   draftData.images.forEach((img) => {
  //     if (img) formData.append('blog[images][]', img);
  //   });
  // }
  // // Only append image_names if present and non-empty
  // if (Array.isArray(draftData.image_names) && draftData.image_names.length > 0) {
  //   draftData.image_names.forEach((name) => {
  //     if (name) formData.append('blog[image_names][]', name);
  //   });
  // }
  if (draftData.keyword_clusters)
    formData.append("blog[keyword_clusters]", draftData.keyword_clusters);
  if (draftData.outlines) formData.append("blog[outlines]", draftData.outlines);
  if (draftData.final_blog)
    formData.append("blog[final_blog]", draftData.final_blog);

  const response = await fetch(
    `${axiosInstance.defaults.baseURL}/api/v1/blogs`,
    {
      method: "POST",
      headers: {
        Authorization: token,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to save blog draft");
  }
  return response.json();
};

export const handleShare = (blogId) => {
  let shareUrl;
  if (blogId) {
    shareUrl = `${window.location.origin}/blog/${blogId}`;
  } else {
    shareUrl = window.location.href;
  }
  navigator.clipboard
    .writeText(shareUrl)
    .then(() => {
      toast.success("Link copied to clipboard!");
    })
    .catch(() => {
      alert("Failed to copy link.");
    });
};

export const getBlogs = async ({
  sort_type = "newest",
  filter_column,
  filter_value,
  page = 1,
  per_page = 500,
} = {}) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");

  const params = new URLSearchParams();
  if (sort_type) params.append("sort_type", sort_type);
  if (filter_column) params.append("filter_column", filter_column);
  if (filter_value) params.append("filter_value", filter_value);
  if (page !== undefined) params.append("page", page);
  if (per_page !== undefined) params.append("per_page", per_page);

  try {
    const response = await axiosInstance.get(
      `/api/v1/blogs?${params.toString()}`,
      {
        headers: {
          Authorization: token,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching blogs:", error.response || error.message);
    throw error;
  }
};

export const generateEmailDescription = async (
  templateName,
  occasion,
  tone,
  furtherDetails,
  agentId,
  sessionId,
  previewImage
) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");
  const payload = {
    preview_image: previewImage,
    request_type: "email",
    additional_data: {
      agent_id: agentId,
      session_id: sessionId,
      tone: tone,
      occasion: occasion,
      template: templateName,
      further_details: furtherDetails,
    },
  };
};

export const getBlogPosts = async ({
  sort_type = "newest",
  filter_column,
  filter_value,
  page = 1,
  per_page = 300,
} = {}) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");
  const params = new URLSearchParams();
  if (sort_type) params.append("sort_type", sort_type);
  if (filter_column) params.append("filter_column", filter_column);
  if (filter_value) params.append("filter_value", filter_value);
  if (page !== undefined) params.append("page", page);
  if (per_page !== undefined) params.append("per_page", per_page);
  try {
    const response = await axiosInstance.get(
      `/api/v1/blog_posts?${params.toString()}`,
      {
        headers: {
          Authorization: token,
        },
      }
    );
    return response.data;
    // pagination_meta: response.pagination_meta
  } catch (error) {
    console.error(
      "Error fetching blog posts:",
      error.response || error.message
    );
    throw error;
  }
};

export const getBlogPostById = async (blogPostId) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");
  try {
    const response = await axiosInstance.get(
      `/api/v1/blog_posts/${blogPostId}`,
      {
        headers: {
          Authorization: token,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching blog post:", error.response || error.message);
    throw error;
  }
};

export const fetchShopifyBlogFromNewApi = async (articleId, blogId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!articleId || !blogId) {
    throw new Error("Article ID and Blog ID are required");
  }

  try {
    const response = await axiosInstance.get(
      `/api/v1/get_blog_post?blog_id=${blogId}&article_id=${articleId}`,
      {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching Shopify blog from new API:", error.response || error.message);
    if (error.response && error.response.status === 404) {
      return null; // Blog not found
    }
    throw error;
  }
};